import React, { useEffect, useState } from "react";
import infoImg from "../../assets/images/info.svg";
import profileImg from "../../assets/images/profile.svg";
import notification from "../../assets/images/notification.svg";
import pubg from "../../assets/images/pubg-corporation-seeklogo.com.svg";
import RL from "../../assets/images/icons8-rocket-league-68.svg";
import CSGO from "../../assets/images/counter-strike-global-offensive-2-logo-svgrepo-com.svg";
import RSS from  "../../assets/images/rainbow-six-siege-logo.svg";
import CoDV from  "../../assets/images/Call-of-Duty-Vanguard.svg";
import WoT from  "../../assets/images/world-of-tanks-3.svg";
import WoWarship from  "../../assets/images/world-of-warships-logo.svg";
import FManager from  "../../assets/images/football-manager-vector-logo.svg";
import Valorant from "../../assets/images/valorant-logo.svg";
import immerselyIcon from "../../logo.svg";
import coins from "../../assets/images/coins.svg";
import "./dashboard.scss";
import { useNavigate } from "react-router-dom";
import { 
  getUserDashboardData, 
  getUserGames, 
  getUserActivities, 
  getUserTasks, 
  getLeaderboard,
  getCurrentUserId,
  setCurrentUserId,
  getCurrentUserProfile
} from "../../services/dashboardService";
import { getAssignmentDownloadUrl, getPlaytesterAssignments, getPublicPlaytests, getPublicPlaytestDownloadUrl } from "../../services/playtesterService";
import { getMyFeedback } from "../../services/feedbackService";
import GameLaunchModal from "../games/GameLaunchModal";
import GameInstructionsModal from "../games/GameInstructionsModal";
import FeedbackModal from "../feedback/FeedbackModal";

const PUBLIC_PLAYTEST_COPY = {
  title: "Public Playtest",
  subtitle: "Help shape upcoming games and earn Immersely credits.",
  heroDescription:
    "Join our public playtest program — similar to Steam Playtest — by playing assigned games, sharing your feedback, and reporting bugs. Immersely credits are awarded after review.",
  rewards: [
    {
      id: "playtest-survey",
      title: "Complete a playtest session",
      description:
        "Play for at least 30 minutes and submit the feedback survey when you're done.",
      reward: "Immersely credits",
      ctaLabel: "Go to My Games",
      ctaTab: "games",
    },
    {
      id: "bug-reports",
      title: "Submit high-quality bug reports",
      description:
        "File clear, reproducible bug reports with steps, expected vs. actual behavior, and severity.",
      reward: "Immersely credits",
      ctaLabel: "Submit Feedback",
      ctaTab: "games",
    },
  ],
  eligibility: [
    "Credits are eligible after Immersely reviews your session or submission.",
    "One credit reward per qualifying playtest session or approved bug report.",
    "Bug reports must be valid, detailed, and not duplicates of known issues.",
    "Approved credits are added to your Immersely account balance.",
  ],
  howItWorks: [
    "Pick a game from My Games and download or launch the build.",
    "Play for at least 30 minutes and explore the game thoroughly.",
    "Submit your feedback survey and any bug reports from the game card.",
    "Earn Immersely credits once your submission passes review.",
  ],
};

function Dashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  
  // State for real data from API
  const [dashboardData, setDashboardData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userAvatar, setUserAvatar] = useState(profileImg);
  const [userGames, setUserGames] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  const [selectedInstructionAssignment, setSelectedInstructionAssignment] = useState(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [publicPlaytestGames, setPublicPlaytestGames] = useState([]);
  const [publicPlaytestLoading, setPublicPlaytestLoading] = useState(false);

  const resolveBuildDownloadUrl = (assignment) => {
    const candidates = [
      assignment?.buildFileUrl,
      assignment?.buildFileURL,
      assignment?.buildUrl,
      assignment?.downloadUrl,
      assignment?.fileUrl,
      assignment?.game?.buildFileUrl,
      assignment?.game?.buildFileURL,
      assignment?.game?.downloadUrl,
      assignment?.gameDetails?.buildFileUrl,
      assignment?.gameDetails?.buildFileURL,
    ];

    const firstValid = candidates.find(
      (value) =>
        typeof value === "string" &&
        value.trim() &&
        value.trim() !== "null" &&
        value.trim() !== "undefined"
    );

    return firstValid ? firstValid.trim() : "";
  };

  const resolveGameId = (value) => {
    if (!value) return "";
    return String(
      value.gameId ||
      value._id ||
      value.id ||
      value.game?._id ||
      value.game?.id ||
      ""
    );
  };

  const findMatchingUserGame = (assignment) => {
    const assignmentGameId = resolveGameId(assignment);
    const assignmentName = (assignment?.gameName || assignment?.name || "").trim().toLowerCase();

    return userGames.find((game) => {
      const gameId = resolveGameId(game);
      const gameName = (game?.gameName || game?.name || "").trim().toLowerCase();

      if (assignmentGameId && gameId && assignmentGameId === gameId) {
        return true;
      }

      if (assignmentName && gameName && assignmentName === gameName) {
        return true;
      }

      return false;
    });
  };

  const enrichAssignmentWithGameData = (assignment) => {
    const matchedGame = findMatchingUserGame(assignment);
    const matchedBuildUrl = resolveBuildDownloadUrl(matchedGame || {});

    return {
      ...(matchedGame || {}),
      ...assignment,
      buildType: assignment?.buildType || matchedGame?.buildType,
      webUrl: assignment?.webUrl || matchedGame?.webUrl,
      requiresAuth:
        typeof assignment?.requiresAuth === "boolean"
          ? assignment.requiresAuth
          : matchedGame?.requiresAuth,
      buildFileUrl:
        assignment?.buildFileUrl ||
        assignment?.buildFileURL ||
        matchedBuildUrl ||
        matchedGame?.buildFileUrl ||
        matchedGame?.buildFileURL ||
        "",
      game: assignment?.game || matchedGame || null,
      matchedGame,
    };
  };

  const handleAssignmentBuildDownload = async (assignment) => {
    const assignmentId = assignment?.assignmentId || assignment?._id || assignment?.id;
    const buildDownloadUrl = resolveBuildDownloadUrl(assignment);
    console.info("[MyAssignments] Download click", {
      assignmentId,
      gameId: assignment?.gameId,
      buildType: assignment?.buildType,
      resolvedBuildDownloadUrl: buildDownloadUrl,
    });

    const signedDownloadUrl = await getAssignmentDownloadUrl(assignmentId);
    if (signedDownloadUrl) {
      window.location.href = signedDownloadUrl;
      return;
    }

    if (buildDownloadUrl) {
      // Fallback for environments where download endpoint isn't available yet.
      window.location.href = buildDownloadUrl;
      return;
    }

    console.error("[MyAssignments] Missing build download URL on click", assignment);
  };

  // Fallback data for playtester stats (used when API data is loading)
  const defaultPlaytesterData = {
    name: localStorage.getItem("name") || "Demo User",
    level: "Expert Playtester",
    joinDate: "March 2024",
    totalSessions: 47,
    totalHours: 156,
    creditsEarned: 2340,
    cashEarned: 0.00,
    rank: "Gold",
    nextRank: "Platinum",
    rankProgress: 75,
  };

  // Use real data if available, otherwise use fallback
  const playtesterData = userProfile ? {
    name: userProfile.name || localStorage.getItem("name") || "Demo User",
    level: userProfile.level || "Expert Playtester",
    joinDate: userProfile.memberSince ? new Date(userProfile.memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : "March 2024",
    totalSessions: userProfile.totalSessions || 0,
    totalHours: userProfile.totalHours || 0,
    creditsEarned: userProfile.creditsEarned || 0,
    cashEarned: userProfile.cashEarned || 0.00,
    rank: userProfile.rank || "Bronze",
    nextRank: userProfile.nextRank || "Silver",
    rankProgress: userProfile.rankProgress || 0,
  } : (dashboardData ? {
    name: dashboardData.user.name || localStorage.getItem("name") || "Demo User",
    level: dashboardData.user.level || "Expert Playtester",
    joinDate: dashboardData.user.memberSince ? new Date(dashboardData.user.memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : "March 2024",
    totalSessions: dashboardData.user.totalSessions || 0,
    totalHours: dashboardData.user.totalHours || 0,
    creditsEarned: dashboardData.user.creditsEarned || 0,
    cashEarned: dashboardData.user.cashEarned || 0.00,
    rank: dashboardData.user.rank || "Bronze",
    nextRank: dashboardData.user.nextRank || "Silver",
    rankProgress: dashboardData.user.rankProgress || 0,
  } : defaultPlaytesterData);

  // Game image mapping for fallback
  // Note: Game names are case-insensitive when matching
  const gameImageMap = {
    "player unknown battleground": pubg,
    "pubg": pubg,
    "rocket league": RL,
    "rainbow six siege": RSS,
    "call of duty: vanguard": CoDV,
    "call of duty vanguard": CoDV,
    "counter-strike: go": CSGO,
    "counter-strike go": CSGO,
    "counter-strike 2": CSGO,
    "counter strike 2": CSGO,
    "csgo": CSGO,
    "cs2": CSGO,
    "football manager 2022": FManager,
    "world of tanks": WoT,
    "world of warship": WoWarship,
    "world of warships": WoWarship,
    "valorant": Valorant,
  };

  // Function to get game image (case-insensitive matching)
  const getGameImage = (gameName) => {
    if (!gameName) return immerselyIcon;
    
    // Normalize the game name: trim whitespace and convert to lowercase
    const normalizedName = gameName.trim().toLowerCase();
    
    // Try exact match first
    if (gameImageMap[normalizedName]) {
      return gameImageMap[normalizedName];
    }
    
    // Try partial match (in case API returns variations)
    const mapKeys = Object.keys(gameImageMap);
    const matchingKey = mapKeys.find(key => 
      normalizedName.includes(key) || key.includes(normalizedName)
    );
    
    return matchingKey ? gameImageMap[matchingKey] : immerselyIcon;
  };

  // Process games data from API
  const currentGamesList = userGames.filter(game => 
    game.status === "In Progress" || game.status === "Completed"
  ).map(game => ({
    ...game,
    image: game.gameImage || getGameImage(game.gameName),
    name: game.gameName,
    genre: game.genre || "Unknown",
    sessions: game.sessionsCompleted || 0,
    feedback: game.feedbackStatus || "Not Started",
    deadline: game.deadline ? new Date(game.deadline).toLocaleDateString() : "No deadline"
  }));

  const newGamesList = userGames.filter(game => 
    game.status === "Ready to Start"
  ).map(game => ({
    ...game,
    image: game.gameImage || getGameImage(game.gameName),
    name: game.gameName,
    genre: game.genre || "Unknown",
    sessions: game.sessionsCompleted || 0,
    feedback: game.feedbackStatus || "Not Started",
    deadline: game.deadline ? new Date(game.deadline).toLocaleDateString() : "No deadline"
  }));

  // Process achievements data from API
  const achievements = dashboardData?.recentAchievements || [
    { name: "First Steps", description: "Complete your first gaming session", earned: true },
    { name: "Feedback Master", description: "Submit 10 detailed feedback reports", earned: true },
    { name: "Marathon Gamer", description: "Play for 100+ hours total", earned: true },
    { name: "Genre Explorer", description: "Test games from 5 different genres", earned: false },
    { name: "Community Helper", description: "Help 5 new playtesters", earned: false },
    { name: "Beta Legend", description: "Test 50 different games", earned: false },
  ];

  // Fetch all dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, try to get the current user profile to get user ID and updated profile data
        const profileResponse = await getCurrentUserProfile();
        
        let userId = getCurrentUserId();
        
        if (profileResponse && profileResponse._id) {
          userId = profileResponse._id;
          setUserProfile(profileResponse);
          // Set avatar from profile, fallback to default
          if (profileResponse.avatar && profileResponse.avatar !== 'Profile') {
            setUserAvatar(profileResponse.avatar);
          } else {
            setUserAvatar(profileImg);
          }
        } else if (!userId) {
          // If we still don't have a user ID, we can't proceed
          setError("Unable to identify user. Please try logging in again.");
          return;
        }

        // Fetch all dashboard data in parallel
        const [dashboardResponse, gamesResponse, activitiesResponse, tasksResponse, leaderboardResponse] = await Promise.all([
          getUserDashboardData(userId),
          getUserGames(userId),
          getUserActivities(userId),
          getUserTasks(userId),
          getLeaderboard()
        ]);

        // Update state with fetched data
        if (dashboardResponse) {
          setDashboardData(dashboardResponse);
        }

        if (gamesResponse) {
          setUserGames(gamesResponse);
        }

        if (activitiesResponse) {
          setRecentActivity(activitiesResponse);
        }

        if (tasksResponse) {
          setUpcomingTasks(tasksResponse);
        }

        if (leaderboardResponse) {
          setLeaderboardData(leaderboardResponse.leaderboard || []);
        }

        // Fetch playtester assignments
        const assignmentsResponse = await getPlaytesterAssignments();
        if (assignmentsResponse && Array.isArray(assignmentsResponse)) {
          setAssignments(assignmentsResponse);
        }

      } catch (err) {
        // console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Fetch feedback history when feedback tab is active
  useEffect(() => {
    const fetchFeedbackHistory = async () => {
      if (activeTab === "feedback") {
        try {
          setFeedbackLoading(true);
          const response = await getMyFeedback({ limit: 50, offset: 0 });
          if (response && response.feedbacks) {
            setFeedbackHistory(response.feedbacks);
          }
        } catch (err) {
          // Silently fail - feedback history is optional
          setFeedbackHistory([]);
        } finally {
          setFeedbackLoading(false);
        }
      }
    };

    fetchFeedbackHistory();
  }, [activeTab]);

  useEffect(() => {
    const fetchPublicPlaytests = async () => {
      if (activeTab !== "publicPlaytest") {
        return;
      }

      try {
        setPublicPlaytestLoading(true);
        const games = await getPublicPlaytests();
        setPublicPlaytestGames(Array.isArray(games) ? games : []);
      } catch (err) {
        setPublicPlaytestGames([]);
      } finally {
        setPublicPlaytestLoading(false);
      }
    };

    fetchPublicPlaytests();
  }, [activeTab]);

  const toPublicPlaytestAssignment = (game) => ({
    gameId: game.gameId,
    gameName: game.gameName,
    buildType: game.buildType,
    webUrl: game.webUrl,
    buildFileUrl: game.buildFileUrl,
    accessToken: game.accessToken,
    requiresAuth: game.requiresAuth,
    instructions: game.instructions,
    deadline: game.deadline,
    isPublicPlaytest: true,
  });

  const handlePublicPlaytestDownload = async (game) => {
    const signedDownloadUrl = await getPublicPlaytestDownloadUrl(game.gameId);
    if (signedDownloadUrl) {
      window.location.href = signedDownloadUrl;
      return;
    }

    if (game.buildFileUrl) {
      window.location.href = game.buildFileUrl;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('name');
    localStorage.removeItem('username');
    localStorage.removeItem('immerselyEmail');
    localStorage.removeItem('userId');
    localStorage.removeItem('userData');
    navigate('/');
  };

  const WatchAppURL = "https://play.google.com/store/apps/details?id=ai.immersely.immersely&hl=en-US&ah=RzHLH8j1fwVpyqskEGJIxuPy4X8";
  const DesktopURL = "https://download.overwolf.com/install/Download?ExtensionId=jcbcafmncfonjjnajpmohkjobajblmikpeifkjag&app_channel=Upgrade";

  // Debug mode - set to true to see what data is being loaded
  const DEBUG_MODE = false;

  // Debug component
  const DebugPanel = () => {
    if (!DEBUG_MODE) return null;
    
    return (
      <div style={{ 
        position: 'fixed', 
        top: '10px', 
        right: '10px', 
        background: 'rgba(0,0,0,0.8)', 
        color: 'white', 
        padding: '10px', 
        borderRadius: '5px',
        fontSize: '12px',
        maxWidth: '300px',
        maxHeight: '400px',
        overflow: 'auto',
        zIndex: 1000
      }}>
        <h4>Debug Info</h4>
        <p><strong>User ID:</strong> {getCurrentUserId()}</p>
        <p><strong>Has Token:</strong> {localStorage.getItem('token') ? 'Yes' : 'No'}</p>
        <p><strong>User Profile:</strong> {userProfile ? 'Loaded' : 'Not loaded'}</p>
        <p><strong>Dashboard Data:</strong> {dashboardData ? 'Loaded' : 'Not loaded'}</p>
        <p><strong>Games Count:</strong> {userGames.length}</p>
        <p><strong>Activities Count:</strong> {recentActivity.length}</p>
        <p><strong>Tasks Count:</strong> {upcomingTasks.length}</p>
        <p><strong>Leaderboard Count:</strong> {leaderboardData.length}</p>
        {userProfile && (
          <div>
            <h5>Profile Data:</h5>
            <pre style={{ fontSize: '10px', overflow: 'auto' }}>
              {JSON.stringify(userProfile, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  // Loading component
  if (loading) {
    return (
      <div className="modern-dashboard-container">
        <div className="loading-container" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div className="spinner" style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 2s linear infinite'
          }}></div>
          <p style={{ color: '#666', fontSize: '18px' }}>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Error component
  if (error) {
    return (
      <div className="modern-dashboard-container">
        <div className="error-container" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{ color: '#e74c3c', fontSize: '24px', fontWeight: 'bold' }}>⚠️ Error</div>
          <p style={{ color: '#666', fontSize: '16px', textAlign: 'center', maxWidth: '400px' }}>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              padding: '10px 20px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-inner">
            <div className="logo-section">
              <img src={infoImg} alt="Immersely Logo" className="logo-img" />
              <span className="logo-text">IMMERSELY</span>
              <div className="portal-badge">Playtester Portal</div>
            </div>

            <div className="header-actions">
              <button className="header-btn">
                <img src={notification} alt="notifications" />
              </button>
              <div className="profile-dropdown">
                <button className="header-btn profile-btn">
                  <img src={userAvatar || profileImg} alt="profile" onError={(e) => { e.target.src = profileImg; }} />
                </button>
                <div className="dropdown-menu">
                  <div className="dropdown-item" onClick={() => navigate('/rewards')}>Rewards</div>
                  <div className="dropdown-item">Invite Friends</div>
                  <div className="dropdown-item" onClick={() => navigate('/settings')}>Settings</div>
                  <div className="dropdown-item" onClick={handleLogout}>Logout</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Welcome Section */}
        <div className="welcome-section">
          <div className="welcome-header">
            <div>
              <h1 className="welcome-title">Welcome back, {playtesterData.name}!</h1>
              <p className="welcome-subtitle">Ready to shape the future of gaming?</p>
            </div>
            <div className="earnings-display">
              <div className="earnings-item">
                <div className="credits-amount">{playtesterData.creditsEarned}</div>
                <div className="credits-label">Credits Earned</div>
              </div>
              <div className="earnings-item">
                <div className="cash-amount">${parseFloat(playtesterData.cashEarned || 0).toFixed(2)}</div>
                <div className="cash-label">Cash Earned</div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card stat-card-purple">
              <div className="stat-content">
                <div className="stat-info">
                  <p className="stat-label">Rank</p>
                  <p className="stat-value">{playtesterData.rank}</p>
                </div>
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                    <path d="M14 9h1.5a2.5 2.5 0 0 1 0 5H14"></path>
                    <path d="M12 12l9-9"></path>
                    <path d="M21 3l-9 9"></path>
                  </svg>
                </div>
              </div>
              <div className="progress-section">
                <div className="progress-header">
                  <span className="progress-text">Progress to {playtesterData.nextRank}</span>
                  <span className="progress-percentage">{playtesterData.rankProgress}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${playtesterData.rankProgress}%` }}></div>
                </div>
              </div>
            </div>

            <div className="stat-card stat-card-blue">
              <div className="stat-content">
                <div className="stat-info">
                  <p className="stat-label">Total Sessions</p>
                  <p className="stat-value">{playtesterData.totalSessions}</p>
                </div>
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 12H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2"></path>
                    <path d="M18 12h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2"></path>
                    <rect width="12" height="12" x="6" y="8" rx="2"></rect>
                  </svg>
                </div>
              </div>
            </div>

            <div className="stat-card stat-card-green">
              <div className="stat-content">
                <div className="stat-info">
                  <p className="stat-label">Hours Played</p>
                  <p className="stat-value">{playtesterData.totalHours}</p>
                </div>
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12,6 12,12 16,14"></polyline>
                  </svg>
                </div>
              </div>
            </div>

            <div className="stat-card stat-card-orange">
              <div className="stat-content">
                <div className="stat-info">
                  <p className="stat-label">Member Since</p>
                  <p className="stat-value-small">{playtesterData.joinDate}</p>
                </div>
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Tabs */}
        <div className="tabs-container">
          <div className="tabs-list">
            <button 
              className={`tab-trigger ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9,22 9,12 15,12 15,22"></polyline>
              </svg>
              Overview
            </button>
            <button 
              className={`tab-trigger ${activeTab === "games" ? "active" : ""}`}
              onClick={() => setActiveTab("games")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 12H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2"></path>
                <path d="M18 12h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2"></path>
                <rect width="12" height="12" x="6" y="8" rx="2"></rect>
              </svg>
              My Games
            </button>
            <button
              className={`tab-trigger ${activeTab === "publicPlaytest" ? "active" : ""}`}
              onClick={() => setActiveTab("publicPlaytest")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 8v4l3 3"></path>
              </svg>
              Public Playtest
            </button>
            <button 
              className={`tab-trigger ${activeTab === "achievements" ? "active" : ""}`}
              onClick={() => setActiveTab("achievements")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                <path d="M14 9h1.5a2.5 2.5 0 0 1 0 5H14"></path>
                <path d="M12 12l9-9"></path>
                <path d="M21 3l-9 9"></path>
              </svg>
              Achievements
            </button>
            <button 
              className={`tab-trigger ${activeTab === "feedback" ? "active" : ""}`}
              onClick={() => setActiveTab("feedback")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              Feedback
            </button>
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="tab-content">
              <div className="overview-grid">
                {/* Active Games */}
                <div className="overview-main">
                  <div className="dashboard-card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="5,3 19,12 5,21"></polygon>
                        </svg>
                        Active Games
                      </h3>
                    </div>
                    <div className="card-content">
                      {currentGamesList.map((game, index) => (
                        <div key={index} className="game-item">
                          <div className="game-header">
                            <div className="game-info">
                              <h4 className="game-title">{game.name}</h4>
                              <p className="game-genre">{game.genre}</p>
                            </div>
                            <div className={`status-badge status-${game.status.toLowerCase().replace(' ', '-')}`}>
                              {game.status}
                            </div>
                          </div>
                          <div className="game-progress">
                            <div className="progress-header">
                              <span>Progress</span>
                              <span>{game.progress}%</span>
                            </div>
                            <div className="progress-bar">
                              <div className="progress-fill" style={{ width: `${game.progress}%` }}></div>
                            </div>
                            <div className="game-meta">
                              <span>Sessions: {game.sessions}</span>
                              <span>Due: {game.deadline}</span>
                            </div>
                          </div>
                          <div className="game-actions">
                            <button className="btn btn-primary">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="5,3 19,12 5,21"></polygon>
                              </svg>
                              Play
                            </button>
                            <button className="btn btn-secondary">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14,2 14,8 20,8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10,9 9,9 8,9"></polyline>
                              </svg>
                              Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="overview-sidebar">
                  {/* Upcoming Tasks */}
                  <div className="dashboard-card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        Upcoming Tasks
                      </h3>
                    </div>
                    <div className="card-content">
                      {upcomingTasks.map((task, index) => (
                        <div key={index} className="task-item">
                          <div className="task-header">
                            <h4 className="task-title">{task.taskDescription || task.task}</h4>
                            <div className={`priority-badge priority-${(task.priority || 'medium').toLowerCase()}`}>
                              {task.priority || 'Medium'}
                            </div>
                          </div>
                          <p className="task-game">{task.gameName || task.game}</p>
                          <p className="task-due">
                            Due: {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : (task.due || 'No deadline')}
                          </p>
                        </div>
                      ))}
                      {upcomingTasks.length === 0 && (
                        <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                          No upcoming tasks.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="dashboard-card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"></polyline>
                        </svg>
                        Recent Activity
                      </h3>
                    </div>
                    <div className="card-content">
                      {recentActivity.slice(0, 4).map((activity, index) => (
                        <div key={index} className="activity-item">
                          <div className={`activity-dot activity-${activity.activityType || activity.type}`}></div>
                          <div className="activity-content">
                            <p className="activity-action">{activity.actionDescription || activity.action}</p>
                            <p className="activity-meta">
                              {activity.gameName || activity.game} • {
                                activity.activityTime 
                                  ? new Date(activity.activityTime).toLocaleString()
                                  : activity.time
                              }
                            </p>
                            <p className="activity-credits">+{activity.creditsEarned || activity.credits} credits</p>
                          </div>
                        </div>
                      ))}
                      {recentActivity.length === 0 && (
                        <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                          No recent activity found.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Leaderboard */}
                  <div className="dashboard-card">
                    <div className="card-header">
                      <h3 className="card-title">Leader Board</h3>
                    </div>
                    <div className="card-content">
                      {leaderboardData.map((player, index) => (
                        <div key={index} className="leaderboard-item">
                          <img className="leaderboard-avatar" src={player.avatar || profileImg} alt="profile" onError={(e) => { e.target.src = profileImg; }} />
                          <div className="leaderboard-info">
                            <div className="leaderboard-name">{player.name || player.username}</div>
                            <div className="leaderboard-coins">
                              <img src={coins} alt="coins" className="coin-icon" />
                              {player.leaderboardCoins || player.coins || 0}
                            </div>
                          </div>
                        </div>
                      ))}
                      {leaderboardData.length === 0 && (
                        <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                          No leaderboard data available.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Download Reminder */}
              <div className="download-reminder">
                <p className="reminder-title">In case you don't have them:</p>
                <div className="reminder-links">
                  <a href={WatchAppURL} className="reminder-link" target="_blank" rel="noopener noreferrer">
                    Download the Immersely Watch App
                  </a>
                  <a href={DesktopURL} className="reminder-link" target="_blank" rel="noopener noreferrer">
                    Download the Immersely Desktop App
                  </a>
                </div>
              </div>

              {/* Video Section */}
              <div className="video-section">
                <div className="youtube-container">
                  <iframe 
                    width="560" 
                    height="315" 
                    src="https://www.youtube.com/embed/BaEMa8KMmqM?si=5sfkeastt-F65_w9" 
                    title="YouTube video player" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                    referrerPolicy="strict-origin-when-cross-origin" 
                    allowFullScreen>
                  </iframe>
                </div>
              </div>

              {/* View Moments Button */}
              <div className="view-moments-section">
                <button className="btn btn-moments" onClick={() => navigate('/upload-new-video')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14.828 14.828a4 4 0 0 1-5.656 0"></path>
                    <path d="M9 9a3 3 0 1 1 6 0l-1-1v6h-4v-6l-1 1"></path>
                    <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"></path>
                  </svg>
                  VIEW MOMENTS
                </button>
              </div>
            </div>
          )}

          {/* Games Tab */}
          {activeTab === "games" && (
            <div className="tab-content">
              <div className="games-grid">
                <h3 className="section-title">My Assignments</h3>
                {assignments.length > 0 ? (
                  assignments
                    .filter((assignment) => {
                      // Show all assignments with status 'assigned' or 'in_progress'
                      const hasValidStatus = assignment.status === "assigned" || assignment.status === "in_progress";
                      return hasValidStatus;
                    })
                    .map((assignment, index) => {
                      const assignmentWithGameData = enrichAssignmentWithGameData(assignment);
                      const buildDownloadUrl = resolveBuildDownloadUrl(assignmentWithGameData);
                      return (
                      <div key={assignment.assignmentId || index} className="game-card-detailed">
                        <div className="game-card-header">
                          <div className="game-card-info">
                            <div className="game-image-placeholder" style={{ width: "60px", height: "60px", background: "#374151", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "24px" }}>
                              🎮
                            </div>
                            <div>
                              <h3 className="game-card-title">{assignmentWithGameData.gameName || "Game"}</h3>
                              <p className="game-card-genre">{assignmentWithGameData.buildType === "web" ? "Web Game" : "Download"}</p>
                            </div>
                          </div>
                          <div className={`status-badge status-${assignment.status.toLowerCase().replace('_', '-')}`}>
                            {assignment.status === "in_progress" ? "In Progress" : "Assigned"}
                          </div>
                        </div>
                        <div className="game-card-stats">
                          <div className="stat">
                            <p className="stat-label">Sessions Completed</p>
                            <p className="stat-value">{assignment.sessionsCompleted || 0}</p>
                          </div>
                          <div className="stat">
                            <p className="stat-label">Feedback Submitted</p>
                            <p className="stat-value">{assignment.feedbackSubmitted ? "Yes" : "No"}</p>
                          </div>
                          {assignment.deadline && (
                            <div className="stat">
                              <p className="stat-label">Deadline</p>
                              <p className="stat-value">{new Date(assignment.deadline).toLocaleDateString()}</p>
                            </div>
                          )}
                        </div>
                        <div className="game-card-actions">
                          {assignmentWithGameData.buildType === "web" && assignmentWithGameData.webUrl && (
                            <button
                              className="btn btn-primary"
                              onClick={() => {
                                setSelectedAssignment(assignmentWithGameData);
                                setIsLaunchModalOpen(true);
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="5,3 19,12 5,21"></polygon>
                              </svg>
                              Launch Game
                            </button>
                          )}
                          {assignmentWithGameData.buildType === "file" && (
                            <>
                              {buildDownloadUrl && (
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => handleAssignmentBuildDownload(assignmentWithGameData)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7,10 12,15 17,10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                  </svg>
                                  Download Build
                                </button>
                              )}
                              <button
                                className="btn btn-primary"
                                onClick={() => {
                                  setSelectedAssignment(assignmentWithGameData);
                                  setIsLaunchModalOpen(true);
                                }}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polygon points="5,3 19,12 5,21"></polygon>
                                </svg>
                                Launch Game
                              </button>
                            </>
                          )}
                          <button 
                            className="btn btn-secondary"
                            onClick={() => {
                              setSelectedInstructionAssignment(assignment);
                              setIsInstructionsModalOpen(true);
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            </svg>
                            View Instructions
                          </button>
                          <button 
                            className="btn btn-secondary"
                            onClick={() => {
                              setSelectedAssignment(assignment);
                              setIsFeedbackModalOpen(true);
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                            </svg>
                            Feedback
                          </button>
                        </div>
                      </div>
                    );
                    })
                ) : (
                  <div className="no-assignments">
                    <p>No game assignments at this time.</p>
                  </div>
                )}

                <h3 className="section-title">Current Games Supported</h3>
                {currentGamesList.map((game, index) => (
                  <div key={index} className="game-card-detailed">
                    <div className="game-card-header">
                      <div className="game-card-info">
                        <img src={game.image} alt={game.name} className="game-image" />
                        <div>
                          <h3 className="game-card-title">{game.name}</h3>
                          <p className="game-card-genre">{game.genre}</p>
                        </div>
                      </div>
                      <div className={`status-badge status-${game.status.toLowerCase().replace(' ', '-')}`}>
                        {game.status}
                      </div>
                    </div>
                    <div className="game-card-stats">
                      <div className="stat">
                        <p className="stat-label">Progress</p>
                        <div className="progress-container">
                          <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${game.progress}%` }}></div>
                          </div>
                          <span className="progress-text">{game.progress}%</span>
                        </div>
                      </div>
                      <div className="stat">
                        <p className="stat-label">Sessions Completed</p>
                        <p className="stat-value">{game.sessions}</p>
                      </div>
                      <div className="stat">
                        <p className="stat-label">Deadline</p>
                        <p className="stat-value">{game.deadline}</p>
                      </div>
                    </div>
                    <div className="game-card-actions">
                      <button className="btn btn-primary">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="5,3 19,12 5,21"></polygon>
                        </svg>
                        Launch Game
                      </button>
                      <button className="btn btn-secondary">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        </svg>
                        View Instructions
                      </button>
                      <button className="btn btn-secondary">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7,10 12,15 17,10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download
                      </button>
                    </div>
                  </div>
                ))}

                <h3 className="section-title">New Games Incoming</h3>
                {newGamesList.map((game, index) => (
                  <div key={index} className="game-card-detailed">
                    <div className="game-card-header">
                      <div className="game-card-info">
                        <img src={game.image} alt={game.name} className="game-image" />
                        <div>
                          <h3 className="game-card-title">{game.name}</h3>
                          <p className="game-card-genre">{game.genre}</p>
                          <p className="game-platform">PC</p>
                        </div>
                      </div>
                      <div className={`status-badge status-${game.status.toLowerCase().replace(' ', '-')}`}>
                        {game.status}
                      </div>
                    </div>
                    <div className="game-card-stats">
                      <div className="stat">
                        <p className="stat-label">Available</p>
                        <p className="stat-value">{game.deadline}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Public Playtest Tab */}
          {activeTab === "publicPlaytest" && (
            <div className="tab-content">
              <div className="public-playtest">
                <div className="public-playtest-hero">
                  <div className="public-playtest-hero-badge">Open to playtesters</div>
                  <h2 className="public-playtest-title">{PUBLIC_PLAYTEST_COPY.title}</h2>
                  <p className="public-playtest-subtitle">{PUBLIC_PLAYTEST_COPY.subtitle}</p>
                  <p className="public-playtest-description">{PUBLIC_PLAYTEST_COPY.heroDescription}</p>
                </div>

                <div className="public-playtest-games-section">
                  <h3 className="section-title">Available Public Playtests</h3>
                  {publicPlaytestLoading ? (
                    <div className="public-playtest-loading">
                      <div className="loading-spinner"></div>
                      <p>Loading public playtests...</p>
                    </div>
                  ) : publicPlaytestGames.length === 0 ? (
                    <div className="public-playtest-empty dashboard-card">
                      <div className="card-content">
                        <p>No public playtests are available right now.</p>
                        <p className="public-playtest-empty-hint">Check back soon for new developer builds.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="public-playtest-games-grid">
                      {publicPlaytestGames.map((game) => (
                        <div key={game.gameId} className="game-card-detailed public-playtest-game-card">
                          <div className="game-card-header">
                            <div className="game-card-info">
                              <div className="game-image-placeholder">
                                🎮
                              </div>
                              <div>
                                <h3 className="game-card-title">{game.gameName}</h3>
                                <p className="game-card-genre">
                                  {game.genre || "Game"} · {game.buildType === "web" ? "Web" : "Download"}
                                </p>
                              </div>
                            </div>
                            {game.versionLabel && (
                              <div className="status-badge status-in-progress">
                                {game.versionLabel}
                              </div>
                            )}
                          </div>
                          {game.description && (
                            <p className="public-playtest-game-description">{game.description}</p>
                          )}
                          <div className="game-card-actions">
                            {game.buildType === "web" && game.webUrl && (
                              <button
                                className="btn btn-primary"
                                onClick={() => {
                                  setSelectedAssignment(toPublicPlaytestAssignment(game));
                                  setIsLaunchModalOpen(true);
                                }}
                              >
                                Launch Game
                              </button>
                            )}
                            {game.buildType === "file" && (
                              <>
                                {game.buildFileUrl && (
                                  <button
                                    className="btn btn-secondary"
                                    onClick={() => handlePublicPlaytestDownload(game)}
                                  >
                                    Download Build
                                  </button>
                                )}
                                <button
                                  className="btn btn-primary"
                                  onClick={() => {
                                    setSelectedAssignment(toPublicPlaytestAssignment(game));
                                    setIsLaunchModalOpen(true);
                                  }}
                                >
                                  Launch Game
                                </button>
                              </>
                            )}
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                setSelectedAssignment(toPublicPlaytestAssignment(game));
                                setIsFeedbackModalOpen(true);
                              }}
                            >
                              Submit Feedback
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="public-playtest-rewards">
                  {PUBLIC_PLAYTEST_COPY.rewards.map((reward) => (
                    <div key={reward.id} className="public-playtest-reward-card dashboard-card">
                      <div className="card-content">
                        <div className="public-playtest-reward-header">
                          <h3 className="public-playtest-reward-title">{reward.title}</h3>
                          <span className="public-playtest-reward-badge">{reward.reward}</span>
                        </div>
                        <p className="public-playtest-reward-description">{reward.description}</p>
                        <p className="public-playtest-reward-note">Eligible reward · subject to review</p>
                        <button
                          className="btn btn-primary"
                          onClick={() => setActiveTab(reward.ctaTab)}
                        >
                          {reward.ctaLabel}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="public-playtest-sections">
                  <div className="public-playtest-section dashboard-card">
                    <div className="card-header">
                      <h3 className="card-title">How it works</h3>
                    </div>
                    <div className="card-content">
                      <ol className="public-playtest-steps">
                        {PUBLIC_PLAYTEST_COPY.howItWorks.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  <div className="public-playtest-section dashboard-card">
                    <div className="card-header">
                      <h3 className="card-title">Eligibility &amp; review</h3>
                    </div>
                    <div className="card-content">
                      <ul className="public-playtest-eligibility">
                        {PUBLIC_PLAYTEST_COPY.eligibility.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="public-playtest-actions">
                  <button className="btn btn-primary" onClick={() => setActiveTab("games")}>
                    Start a playtest
                  </button>
                  <button className="btn btn-secondary" onClick={() => setActiveTab("feedback")}>
                    View feedback history
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Achievements Tab */}
          {activeTab === "achievements" && (
            <div className="tab-content">
              <div className="achievements-grid">
                {achievements.map((achievement, index) => (
                  <div key={index} className={`achievement-card ${achievement.earned ? 'earned' : ''}`}>
                    <div className="achievement-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                        <path d="M14 9h1.5a2.5 2.5 0 0 1 0 5H14"></path>
                        <path d="M12 12l9-9"></path>
                        <path d="M21 3l-9 9"></path>
                      </svg>
                    </div>
                    <h3 className="achievement-name">{achievement.name}</h3>
                    <p className="achievement-description">{achievement.description}</p>
                    {achievement.earned && (
                      <div className="earned-badge">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 12l2 2 4-4"></path>
                          <circle cx="12" cy="12" r="10"></circle>
                        </svg>
                        Earned
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feedback Tab */}
          {activeTab === "feedback" && (
            <div className="tab-content">
              <div className="dashboard-card">
                <div className="card-header">
                  <h3 className="card-title">Feedback History</h3>
                </div>
                <div className="card-content">
                  {feedbackLoading ? (
                    <div className="feedback-loading">
                      <div className="loading-spinner"></div>
                      <p>Loading feedback history...</p>
                    </div>
                  ) : feedbackHistory.length === 0 ? (
                    <div className="feedback-empty">
                      <p>No feedback submitted yet.</p>
                      <p className="feedback-empty-hint">Submit feedback for games you've played from the "Assigned" tab.</p>
                    </div>
                  ) : (
                    feedbackHistory.map((feedback, index) => (
                      <div key={feedback.feedbackId || index} className="feedback-item">
                        <div className="feedback-header">
                          <h3 className="feedback-game-title">{feedback.gameName || "Unknown Game"}</h3>
                          <div className="status-badge status-submitted">
                            Submitted
                          </div>
                        </div>
                        {feedback.comments && (
                          <p className="feedback-comments">{feedback.comments}</p>
                        )}
                        {feedback.bugReports && Array.isArray(feedback.bugReports) && feedback.bugReports.length > 0 && (
                          <div className="feedback-bugs">
                            <strong>Bug Reports:</strong> {feedback.bugReports.length}
                          </div>
                        )}
                        {feedback.suggestions && Array.isArray(feedback.suggestions) && feedback.suggestions.length > 0 && (
                          <div className="feedback-suggestions">
                            <strong>Suggestions:</strong> {feedback.suggestions.length}
                          </div>
                        )}
                        {feedback.submittedAt && (
                          <p className="feedback-date">
                            Submitted: {new Date(feedback.submittedAt).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Game Launch Modal */}
      {isLaunchModalOpen && selectedAssignment && (
        <GameLaunchModal
          assignment={selectedAssignment}
          isOpen={isLaunchModalOpen}
          onClose={() => {
            setIsLaunchModalOpen(false);
            setSelectedAssignment(null);
          }}
        />
      )}

      {/* Feedback Modal */}
      {isFeedbackModalOpen && selectedAssignment && (
        <FeedbackModal
          assignment={selectedAssignment}
          isOpen={isFeedbackModalOpen}
          onClose={() => {
            setIsFeedbackModalOpen(false);
            setSelectedAssignment(null);
          }}
          onSuccess={(response) => {
            // Refresh assignments to update feedbackSubmitted status
            getPlaytesterAssignments().then((assignments) => {
              if (assignments && Array.isArray(assignments)) {
                setAssignments(assignments);
              }
            });
            // Refresh feedback history if on feedback tab
            if (activeTab === "feedback") {
              getMyFeedback({ limit: 50, offset: 0 }).then((response) => {
                if (response && response.feedbacks) {
                  setFeedbackHistory(response.feedbacks);
                }
              }).catch(() => {
                // Silently fail
              });
            }
          }}
        />
      )}

      {/* Game Instructions Modal */}
      {isInstructionsModalOpen && selectedInstructionAssignment && (
        <GameInstructionsModal
          isOpen={isInstructionsModalOpen}
          onClose={() => {
            setIsInstructionsModalOpen(false);
            setSelectedInstructionAssignment(null);
          }}
          gameName={selectedInstructionAssignment.gameName}
          instructions={selectedInstructionAssignment.instructions}
        />
      )}
    </div>
  );
}

export default Dashboard;
